import React, { useState, useEffect } from 'react';
import InventoryHistoryEvents from '../../business_types/rental/history/InventoryHistoryEvents';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import api from '../../api/api';

const ClientRentalHistory = ({ clientId }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState('summary');

  useEffect(() => {
    if (clientId) {
      fetchRentalHistory();
    }
  }, [clientId]);

  const fetchRentalHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const [historyResponse] = await Promise.all([
        api.get(`/clients/${clientId}/rental-history`)
      ]);

      setHistoryData(historyResponse.data);
    } catch (err) {
      console.error('❌ Ошибка загрузки истории аренд:', err);
      console.error('❌ Детали ошибки:', err.response?.data);
      setError(err.response?.data?.detail || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch {
      return 'Неверная дата';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'активна':
      case 'active':
        return 'primary';
      case 'возвращен':
      case 'completed':
        return 'success';
      case 'просрочен':
      case 'overdue':
        return 'error';
      case 'отменен':
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'активна':
      case 'active':
        return <AccessTimeIcon fontSize="small" />;
      case 'возвращен':
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'просрочен':
      case 'overdue':
        return <WarningIcon fontSize="small" />;
      case 'отменен':
      case 'cancelled':
        return <ErrorIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <IconButton color="inherit" size="small" onClick={fetchRentalHistory}>
          <RefreshIcon />
        </IconButton>
      }>
        {error}
      </Alert>
    );
  }

  if (!historyData) {
    return (
      <Alert severity="info">
        История аренд не найдена
      </Alert>
    );
  }

  return (
    <Box>
      {/* Краткая сводка */}
      <Accordion expanded={expanded === 'summary'} onChange={handleAccordionChange('summary')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">
              Краткая сводка
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {historyData.summary.total_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Всего аренд
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="success.main">
                    {historyData.summary.active_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Активных
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="text.primary">
                    {historyData.summary.completed_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Завершенных
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="error.main">
                    {historyData.summary.overdue_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Просроченных
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(historyData.summary.total_amount_paid)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Общая сумма платежей
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Активные аренды */}
      {false && (
        <Accordion expanded={expanded === 'active'} onChange={handleAccordionChange('active')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTimeIcon color="success" />
              <Typography variant="h6">
                Активные аренды (0)
              </Typography>
            </Box>
          </AccordionSummary>
        </Accordion>
      )}

      {/* Статистика использования */}
      <Accordion expanded={expanded === 'stats'} onChange={handleAccordionChange('stats')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="info" />
            <Typography variant="h6">
              Статистика использования
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Общая статистика
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Всего сделок:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {historyData.summary.total_deals}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Общая сумма:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(historyData.summary.total_amount_paid)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Активных аренд:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {historyData.summary.active_rentals}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Завершенных:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="text.primary">
                        {historyData.summary.completed_rentals}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Просроченных:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        {historyData.summary.overdue_rentals}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Детали использования
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Распределение по статусам:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={`Активных: ${historyData.summary.active_rentals}`}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Завершенных: ${historyData.summary.completed_rentals}`}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Просроченных: ${historyData.summary.overdue_rentals}`}
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Финансовая сводка:
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Всего платежей:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(historyData.summary.total_amount_paid)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Средняя сделка:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {historyData.summary.total_deals > 0
                            ? formatCurrency(historyData.summary.total_amount_paid / historyData.summary.total_deals)
                            : formatCurrency(0)
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* История сделок */}
      <Accordion expanded={expanded === 'deals'} onChange={handleAccordionChange('deals')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="action" />
            <Typography variant="h6">
              История сделок ({historyData.deals.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {historyData.deals.map((deal) => (
              <Card key={deal.deal_id} variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="h6">
                          {deal.deal_number}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(deal.status)}
                          label={deal.status}
                          color={getStatusColor(deal.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {formatDate(deal.start_date)} - {formatDate(deal.end_date)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Активов в аренде: {deal.assets_count}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box textAlign="right">
                        <Typography variant="h6" color="primary">
                          {formatCurrency(deal.total_amount)}
                        </Typography>
                        {deal.amount_due !== deal.total_amount && (
                          <Typography variant="body2" color="text.secondary">
                            К доплате: {formatCurrency(deal.amount_due)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <InventoryHistoryEvents events={deal.inventory_events} />
                  {deal.assets.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Активы:
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Название</TableCell>
                              <TableCell>Артикул</TableCell>
                              <TableCell>Статус</TableCell>
                              <TableCell align="right">Сумма</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deal.assets.map((asset, assetIndex) => (
                              <TableRow key={assetIndex}>
                                <TableCell>{asset.asset_name}</TableCell>
                                <TableCell>{asset.asset_sku || '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    icon={getStatusIcon(asset.status)}
                                    label={asset.status}
                                    color={getStatusColor(asset.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(asset.total_amount)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Последние транзакции */}
      {historyData.recent_transactions && historyData.recent_transactions.length > 0 && (
        <Accordion expanded={expanded === 'transactions'} onChange={handleAccordionChange('transactions')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOnIcon color="success" />
              <Typography variant="h6">
                Последние транзакции ({historyData.recent_transactions.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Категория</TableCell>
                    <TableCell>Описание</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.recent_transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.transaction_type === 'income' ? 'Доход' : 'Расход'}
                          color={transaction.transaction_type === 'income' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{transaction.category}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={transaction.transaction_type === 'income' ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default ClientRentalHistory;
